// Copyright (c) Microsoft. All rights reserved.

import serialize from 'serialize-javascript';
import { trackServiceException } from '../../app/telemetry/appInsights';

export interface ServiceRequest {
    url: string;
    method?: string;
    body?: BodyInit | object;
    headers?: HeadersInit;
}

const noResponseBodyStatusCodes = [204];

const extractEndpointPath = (url: string): string => {
    try {
        return new URL(url, window.location.origin).pathname;
    } catch {
        return url.split('?')[0];
    }
};

const extractStatusCode = (errorMessage: string): number | undefined => {
    const match = errorMessage.match(/^(\d{3}):/);
    return match ? Number(match[1]) : undefined;
};


export class BaseService {

    protected readonly getResponseAsync = async <T>(
        request: ServiceRequest,
        accessToken: string,
        signal?: AbortSignal,
    ): Promise<T> => {
        const { url, method, body, headers: requestHeaders } = request;

        const isFormData = body instanceof FormData;
        const headers = new Headers(requestHeaders);
        headers.set('Authorization', `Bearer ${accessToken}`);

        if (!isFormData && !headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }

        try {
            const response = await fetch(url, {
                method: method ?? 'GET',
                body: body == undefined ? undefined : isFormData ? body : serialize(body),
                headers,
                signal,
            });


            if (!response.ok) {
                const responseText = await response.text();
                const errorMessage = `${response.status}: ${response.statusText}${
                    responseText ? ` => ${responseText}` : ''
                }`;

                throw Object.assign(new Error(errorMessage));
            }

            const result = (noResponseBodyStatusCodes?.includes(response.status) ? {} : await response.json()) as T;
            return result;
        } catch (e: any) {
            const isAborted = signal?.aborted || e?.name === 'AbortError';
            if (!isAborted) {
                const errorMessage = e instanceof Error ? e.message : String(e);
                trackServiceException(e, {
                    source: 'BaseService.getResponseAsync',
                    operation: method ?? 'GET',
                    endpoint: extractEndpointPath(url),
                    statusCode: extractStatusCode(errorMessage),
                    isFatal: true,
                }, {
                    isNetworkError: e instanceof TypeError,
                });
            }

            let additionalErrorMsg = '';
            if (e instanceof TypeError) {
                // fetch() will reject with a TypeError when a network error is encountered.
                additionalErrorMsg =
                    '\n\nPlease check that your backend is running and that it is accessible by the app';
            }
            throw Object.assign(new Error(`${e as string} ${additionalErrorMsg}`));
        }
    };

    protected readonly getBlobResponseAsync = async <T>(
        request: ServiceRequest,
        accessToken: string,
        signal?: AbortSignal,
    ): Promise<T> => {
                const { url, method, body, headers: requestHeaders } = request;
        const isFormData = body instanceof FormData;

        const headers = new Headers(requestHeaders);
        headers.set('Authorization', `Bearer ${accessToken}`);

        if (!isFormData && !headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }

        try {
            const response = await fetch(url, {
                method: method ?? 'GET',
                body: body == undefined ? undefined : isFormData ? body : serialize(body),
                headers,
                signal,
            });


            if (!response.ok) {
                const responseText = await response.text();
                const errorMessage = `${response.status}: ${response.statusText}${
                    responseText ? ` => ${responseText}` : ''
                }`;
                throw Object.assign(new Error(errorMessage));
            }

            return (noResponseBodyStatusCodes?.includes(response.status) ? {} : await response.blob()) as T;
        } catch (e: any) {
            const isAborted = signal?.aborted || e?.name === 'AbortError';
            if (!isAborted) {
                const errorMessage = e instanceof Error ? e.message : String(e);
                trackServiceException(e, {
                    source: 'BaseService.getBlobResponseAsync',
                    operation: method ?? 'GET',
                    endpoint: extractEndpointPath(url),
                    statusCode: extractStatusCode(errorMessage),
                    isFatal: true,
                }, {
                    isNetworkError: e instanceof TypeError,
                });
            }

            let additionalErrorMsg = '';
            if (e instanceof TypeError) {
                // fetch() will reject with a TypeError when a network error is encountered.
                additionalErrorMsg =
                    '\n\nPlease check that your backend is running and that it is accessible by the app';
            }
            throw Object.assign(new Error(`${e as string} ${additionalErrorMsg}`));
        }
    };
}
